import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useTranslation } from 'react-i18next';

const ENDPOINT_URL = '/profile/create-vehicle';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type CreateVehicleInput = paths[url]['post'];

export interface CreateVehicleSuccessResponse {
    message: string;
    data: {
        uuid: string;
        brand: string;
        model: string;
        color: string;
        plate: string;
        seats: number;
        is_primary: boolean;
        created_at: string;
    };
}

export type CreateVehicleRequestInput = {
    brand: string;
    model: string;
    color: string;
    plate: string;
    seats: number;
};

export const useCreateVehicleRequest = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
    const [createdVehicle, setCreatedVehicle] = useState<any | null>(null);

    const request = async (props: CreateVehicleRequestInput) => {
        setLoading(true);
        setError(null);
        setValidationErrors(null);
        setErrorMessage(null);
        setCreatedVehicle(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    ...getCommonParameters(),
                    ...props,
                }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch {
                    error.message = t('common.error-occurred');
                }

                error.statusCode = response.status;
                throw error;
            }

            const rawResponse: CreateVehicleSuccessResponse = await response.json();

            setSuccess(rawResponse.message);
            setCreatedVehicle(rawResponse.data);

            return rawResponse.data;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return {
        request,
        loading,
        error,
        errorMessage,
        lastResponseTime,
        validationErrors,
        success,
        createdVehicle,
    };
};
